/**
 * jump-prediction.ts — v4M12 Jump Prediction Sensors arrival-angle
 * visibility helper.
 *
 * The defender's arrival-angle preview applies on top of the existing
 * `Jump Prediction Sensors` tech. The source-system charge-up layer
 * shipped at v4M5.6 (advance-detection.ts: charging ships forced to
 * 'scan' channel when defender has tech + in-system scanner reach of
 * the source). This module adds the destination-system layer per
 * `docs/TECHTREE.md §9.1` (visibility rule locked v4M12.0, amended
 * 2026-05-30 by MFix34 — see "amendment" below):
 *
 * The viewer empire can see an inbound enemy ship's arrival angle on
 * the destination's Jump Area ring iff
 *   1. viewer has researched the `Jump Prediction Sensors` tech, AND
 *   2. viewer's team has at least one in-system scanner on the
 *      destination system (any ship-or-planet contributor returned by
 *      `collectTeamScannersForSystem` — galaxy-frame LRSA reach does
 *      NOT satisfy the gate), AND
 *   3. the inbound ship is currently charging up OR in flight (once
 *      arrived the angle is moot — emergence is the visible event), AND
 *   4. the inbound ship is owned by an out-of-team empire (no need to
 *      preview teammates' or own jumps).
 *
 * **MFix34 amendment (2026-05-30).** The original v4M12.0 lock gated
 * on `jumpArrivalAngle !== null` ("from-source default angles never
 * leak"). Playtest showed AI never picks angles — every AI dispatch
 * stores `arrivalAngle: null` — so that gate killed the feature in
 * AI-vs-player play. The rule was protecting a phantom advantage: the
 * default-arrival point is deterministic from src/dst geometry, so the
 * defender's lack of info added no real strategic asymmetry. The
 * helper now falls back to the same `Math.atan2(src.y - dst.y, src.x
 * - dst.x)` formula the lander uses in `advance.ts` so the chevron
 * surfaces for every visible inbound, picked or default.
 *
 * v4M12.1 consumes this from `src/render/galaxy-arrival-warnings.ts`;
 * v4M12.2 consumes it from the system view as well.
 */

import { collectTeamScannersForSystem } from './scanners';
import { getTeamMembers } from './teams';
import type {
  EmpireId,
  GameState,
  ShipInstanceId,
  SystemId,
} from '../state/types/index';

/**
 * Per-incoming-jump payload returned to renderers. `ticksRemaining`
 * is exposed for the v4M12-deferred "incoming in N ticks" alert-feed
 * surface but isn't surfaced at v4M12.1 / v4M12.2 — chevrons render
 * statically at the picked angle until the ship arrives.
 */
export interface IncomingArrival {
  shipId: ShipInstanceId;
  attackerEmpireId: EmpireId;
  arrivalAngle: number;
  /**
   * Sum of any remaining charge-up ticks at the source plus in-flight
   * hyperspace ticks. Zero is the emergence tick itself. Never used
   * negatively (the filter excludes arrived ships).
   */
  ticksRemaining: number;
}

/** Tech-tree name string — `Empire.researchedTechs` is keyed by name. */
export const JUMP_PREDICTION_TECH = 'Jump Prediction Sensors';

/**
 * Effective arrival angle for an inbound ship. Returns the picked
 * `jumpArrivalAngle` when non-null, otherwise computes the from-source
 * default per the lander formula in `advance.ts:780` —
 * `Math.atan2(src.y - dst.y, src.x - dst.x)`. Returns `null` when
 * either system is missing (corrupt-state edge — the caller should
 * skip the chevron rather than render at angle 0).
 *
 * Exported so renderers can call it directly when they already know
 * the ship + state shape, and so tests can lock the from-source-
 * default formula independently.
 */
export function effectiveArrivalAngleFor(
  state: GameState,
  ship: { jumpArrivalAngle: number | null; jumpOriginSystemId: SystemId | null; jumpTargetSystemId: SystemId | null },
): number | null {
  if (ship.jumpArrivalAngle !== null) return ship.jumpArrivalAngle;
  if (ship.jumpOriginSystemId === null) return null;
  if (ship.jumpTargetSystemId === null) return null;
  const src = state.systems.get(ship.jumpOriginSystemId);
  const dst = state.systems.get(ship.jumpTargetSystemId);
  if (!src || !dst) return null;
  return Math.atan2(src.y - dst.y, src.x - dst.x);
}

/**
 * Return every inbound enemy jump headed to `destinationSystemId`
 * whose arrival angle is visible to `viewerEmpireId` under the v4M12
 * rule. Order is stable per `state.ships.values()` iteration order
 * (matches the deterministic insertion order Maps guarantee).
 *
 * O(N + S) per call where N is total ship count and S is the cost of
 * `collectTeamScannersForSystem` (one walk of state.ships for the
 * destination system, plus a per-planet pass). At typical galaxy
 * sizes the per-frame cost is negligible; if renderers ever call this
 * across every system per frame, group `state.ships` by destination
 * first and call only for active destinations.
 */
export function incomingJumpArrivalsForViewer(
  state: GameState,
  viewerEmpireId: EmpireId,
  destinationSystemId: SystemId,
): IncomingArrival[] {
  const viewer = state.empires.get(viewerEmpireId);
  if (!viewer) return [];
  if (!viewer.researchedTechs.has(JUMP_PREDICTION_TECH)) return [];

  // Scanner coverage gate. Helper unions teammates internally per
  // v4M3.2 team-vision, so a teammate's scanner satisfies the rule
  // for the viewer without extra plumbing here.
  const scanners = collectTeamScannersForSystem(state, viewerEmpireId, destinationSystemId);
  if (scanners.length === 0) return [];

  // Build a Set for the teammate filter (the array from getTeamMembers
  // is fine for small teams but `has` is the clean read in a hot loop).
  const teamMembers = new Set<EmpireId>(getTeamMembers(state, viewerEmpireId));

  const out: IncomingArrival[] = [];
  for (const ship of state.ships.values()) {
    if (ship.jumpTargetSystemId !== destinationSystemId) continue;
    if (ship.chargeUpTicksRemaining <= 0 && ship.jumpTicksRemaining <= 0) continue;
    if (teamMembers.has(ship.ownerEmpireId)) continue;
    // MFix34 — fall back to the from-source-default angle (same
    // formula the lander uses) when the attacker didn't pick one
    // explicitly. AI dispatches always store `null`, so without this
    // fallback the chevron never appears against AI opponents.
    const angle = effectiveArrivalAngleFor(state, ship);
    if (angle === null) continue;
    out.push({
      shipId: ship.id,
      attackerEmpireId: ship.ownerEmpireId,
      arrivalAngle: angle,
      ticksRemaining: ship.chargeUpTicksRemaining + ship.jumpTicksRemaining,
    });
  }
  return out;
}
